import React, { useMemo, useContext } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Popper from "@mui/material/Popper";
import Link from "@mui/material/Link";
import CircularProgress from "@mui/material/CircularProgress";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import snakeCase from "lodash/snakeCase";

import { ProjectContext } from "../../../contexts/project";
import { EMPTY_STRING_SEARCH_TOKEN } from "../../../constants/search";
import { unitOfMeasureOptions } from "../../../constants/fields/unitOfMeasure";
import useFilteredBillingTypes, {
  getProjectStorageBillingTypeOptions,
} from "../../../hooks/useFilteredBillingTypes";

function BottomAnchoredPopper(popperProps) {
  return (
    <Popper
      {...popperProps}
      placement="bottom-start"
      modifiers={[
        { name: "flip", enabled: false },
        { name: "preventOverflow", enabled: false },
        { name: "offset", options: { offset: [0, 8] } },
      ]}
    />
  );
}

const getFieldType = (fieldValue) => {
  if (!fieldValue) return null;
  const snaked = snakeCase(fieldValue);

  const timestampFields = ['created_at', 'updated_at', 'last_in', 'last_out'];
  if (timestampFields.includes(snaked)) {
    return { type: 'timestamp', format: 'MM/DD/YYYY or MM/YYYY' };
  }

  const dateFormatMap = {
    'expiration_month_day_year': { type: 'date', format: 'MM/DD/YYYY', maxLength: 10 },
    'manufacture_date': { type: 'date', format: 'MM/DD/YYYY', maxLength: 10 },
    'expiration_date': { type: 'date', format: 'MM/YYYY', maxLength: 7 },
    'expiration_month': { type: 'month', format: 'MM', maxLength: 2 },
    'expiration_year': { type: 'year', format: 'YYYY', maxLength: 4 },
  };

  return dateFormatMap[snaked] || null;
};

const formatDateInput = (value, fieldType) => {
  let formatted = value.replace(/[^\d/]/g, '');

  if (fieldType.type === 'month') return formatted.slice(0, 2);
  if (fieldType.type === 'year') return formatted.slice(0, 4);
  if (fieldType.format === 'MM/YYYY') {
    if (formatted.length >= 2 && formatted.charAt(2) !== '/') {
      formatted = formatted.slice(0, 2) + '/' + formatted.slice(2);
    }
    return formatted.slice(0, 7);
  }
  if (formatted.length >= 2 && formatted.charAt(2) !== '/') {
    formatted = formatted.slice(0, 2) + '/' + formatted.slice(2);
  }
  if (formatted.length >= 5 && formatted.charAt(5) !== '/') {
    formatted = formatted.slice(0, 5) + '/' + formatted.slice(5);
  }
  return formatted.slice(0, 10);
};

export default function Step2({
  fields,
  fieldValue,
  fieldLabel,
  findText,
  searchOperator,
  replaceFieldValue,
  onReplaceFieldChange,
  replaceText,
  onReplaceTextChange,
  resultCount,
  searchLoading,
  replaceLoading,
  onBack,
  onReplace,
  onOpenSearchTab,
}) {
  const project = useContext(ProjectContext);
  const filteredBillingTypeOptions = useFilteredBillingTypes(
    getProjectStorageBillingTypeOptions(project?.billingTypes)
  );
  const billingTypeOptions = useMemo(
    () => filteredBillingTypeOptions.map(([id, name]) => ({ id, name })),
    [filteredBillingTypeOptions]
  );
  const displayedFindText =
    findText === EMPTY_STRING_SEARCH_TOKEN ? "Empty" : findText;

  const activeReplaceField = replaceFieldValue || fieldValue;

  const fieldType = useMemo(() => getFieldType(activeReplaceField), [activeReplaceField]);

  const filteredFields = useMemo(() => {
    return (fields || [])
      .filter(f => f.visible && !f.inactive && f.type !== "checkbox" && f.type !== "photo" && !f.disableFillDown)
      .filter(f => !["itemId", "photoCarousel", "rackLocations", "BULK_CHECKBOX", "photoUpload",
        "warehousesFromRackLocations", "warehouseQuantity", "allocatedQuantity",
        "reservedQuantity", "inCartQuantity", "daysLeftToExpire"].includes(f.id));
  }, [fields]);

  const fieldSuggestions = useMemo(() => {
    const safeLabel = f => typeof (f?.label ?? f?.id) === "string" ? (f.label ?? f.id) : String(f.label ?? f.id);
    return filteredFields
      .map(f => ({ id: f.id, name: safeLabel(f) }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }, [filteredFields]);

  const predefinedOptions = useMemo(() => {
    if (!activeReplaceField || !project) return null;
    const snaked = snakeCase(activeReplaceField);

    const fieldOptionsMap = {
      id_cost_center: project.costCenters?.filter(cc => cc.costCenterName).map(cc => ({
        id: cc.costCenterName,
        name: cc.costCenterName,
      })),
      pallet_type: project.palletTypes?.filter(pt => pt.isActive && pt.id).map(pt => ({
        id: pt.id,
        name: pt.name,
      })),
      billing_type: billingTypeOptions,
      unit_of_measure: unitOfMeasureOptions.map(o => ({
        id: o.value,
        name: o.label,
      })),
    };

    return fieldOptionsMap[snaked] || null;
  }, [activeReplaceField, billingTypeOptions, project]);

  const handleDateInputChange = (e) => {
    const formatted = formatDateInput(e.target.value, fieldType);
    onReplaceTextChange(formatted);
  };

  const renderReplaceInput = () => {
    if (predefinedOptions) {
      return (
        <Autocomplete
          fullWidth
          PopperComponent={BottomAnchoredPopper}
          options={predefinedOptions}
          getOptionLabel={option => option.name || ""}
          value={predefinedOptions.find(o => o.id === replaceText) || null}
          onChange={(event, newValue) => onReplaceTextChange(newValue ? newValue.id : "")}
          isOptionEqualToValue={(option, val) => option.id === val?.id}
          ListboxProps={{ style: { maxHeight: "300px", overflow: "auto" } }}
          filterOptions={(options, { inputValue }) => {
            if (!inputValue) return options;
            const term = inputValue.toLowerCase();
            return options.filter(o => (o?.name || "").toLowerCase().includes(term));
          }}
          disabled={searchLoading}
          renderInput={params => (
            <TextField
              {...params}
              label="Replace With"
              variant="outlined"
              size="small"
              fullWidth
            />
          )}
        />
      );
    }

    if (fieldType) {
      return (
        <TextField
          fullWidth
          label="Replace With"
          placeholder={fieldType.format}
          value={replaceText}
          onChange={handleDateInputChange}
          size="small"
          disabled={searchLoading}
          inputProps={{ maxLength: fieldType.maxLength }}
        />
      );
    }

    return (
      <TextField
        fullWidth
        label="Replace With"
        value={replaceText}
        onChange={e => onReplaceTextChange(e.target.value)}
        size="small"
        disabled={searchLoading}
      />
    );
  };

  return (
    <>
      <Box mb={2}>
        <Typography fontWeight={500} fontSize={20}>
          Find and Replace
        </Typography>
      </Box>
      <Box my={3}>
        {searchLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={80}>
            <CircularProgress size={32} />
            <Box ml={2}>
              <Typography variant="body1" color="textSecondary">
                Searching...
              </Typography>
            </Box>
          </Box>
        ) : (
          <>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Found{" "}
              <Typography component="span" fontWeight={600}>
                {resultCount}
              </Typography>{" "}
              instances of{" "}
              <Link
                component="button"
                fontWeight={500}
                onClick={onOpenSearchTab}
                sx={{ cursor: "pointer", verticalAlign: "baseline", display: "inline-flex", alignItems: "center", gap: 0.5, textDecoration: "none" }}
              >
                {fieldLabel || fieldValue} {searchOperator} {displayedFindText}
                <OpenInNewIcon sx={{ fontSize: 16 }} />
              </Link>
            </Typography>
          </>
        )}
        <Box mt={2} display="flex" flexDirection="column" gap={2}>
          <Autocomplete
            fullWidth
            PopperComponent={BottomAnchoredPopper}
            options={fieldSuggestions}
            getOptionLabel={option => option.name || ""}
            value={fieldSuggestions.find(f => f.id === activeReplaceField) || null}
            onChange={(_, newValue) => onReplaceFieldChange(newValue ? newValue.id : "")}
            isOptionEqualToValue={(option, val) => option.id === val?.id}
            disabled={searchLoading}
            ListboxProps={{ style: { maxHeight: "300px", overflow: "auto" } }}
            filterOptions={(options, { inputValue }) => {
              if (!inputValue) return options;
              const term = inputValue.toLowerCase();
              return options.filter(o => (o?.name || "").toLowerCase().includes(term));
            }}
            renderInput={params => (
              <TextField
                {...params}
                label="Replace Field"
                variant="outlined"
                size="small"
                fullWidth
              />
            )}
          />
          {renderReplaceInput()}
        </Box>
      </Box>
      <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
        <Button variant="outlined" color="primary" onClick={onBack} disabled={replaceLoading}>
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onReplace}
          disabled={searchLoading || replaceLoading || !replaceText || resultCount === 0}
          startIcon={replaceLoading && <CircularProgress size={20} />}
          sx={{ minWidth: 140 }}
        >
          Replace All
        </Button>
      </Box>
    </>
  );
}
